import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import socketService from '../services/socketService';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { NOTIFICATION_EVENTS, ORDER_EVENTS, KOT_EVENTS, VENDOR_EVENTS } from '../constants/socketEvents';
import { DisplayContext, formatOrderId } from '../utils/orderFormatter';

interface SocketContextType {
  isConnected: boolean;
  subscribeToOrder: (orderId: number) => void;
  unsubscribeFromOrder: (orderId: number) => void;
  subscribeToKitchen: (kitchenId: number) => void;
  unsubscribeFromKitchen: (kitchenId: number) => void;
  subscribeToVendor: (vendorId: number, callback?: (data: any) => void) => void;
  unsubscribeFromVendor: (vendorId: number) => void;
  subscribeToTable: (tableNumber: string, vendorId: number) => void;
  unsubscribeFromTable: (tableNumber: string, vendorId: number) => void;
  emit: (event: string, data: any) => void;
  on: (event: string, callback: Function) => void;
  off: (event: string, callback: Function) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const { token, user } = useAuth();

  useEffect(() => {
    let mounted = true;
    let connectionAttempt: Promise<void> | null = null;
    
    const initializeSocket = async () => {
      if (token && user && mounted) {
        try {
          console.log('Initializing Socket.IO connection with token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
          
          // Prevent duplicate connection attempts
          if (connectionAttempt) {
            console.log('Connection already in progress, waiting...');
            await connectionAttempt;
            return;
          }
          
          connectionAttempt = socketService.initialize(token);
          await connectionAttempt;
          
          if (!mounted) return;
          
          setIsConnected(true);
          
          // Connection established — log only, no toast
          console.log('Real-time connection established');
          
          // Set up disconnect listener for actual disconnections
          socketService.on('disconnect', (reason: string) => {
            console.log('Socket disconnected:', reason);
            if (reason !== 'io client disconnect' && mounted) {
              setIsConnected(false);
              // Silent — no toast for connection loss
              console.warn('Real-time connection lost:', reason);
            }
          });

          // Set up notification listeners using standardized event names
          socketService.on(NOTIFICATION_EVENTS.GENERAL, (notification: any) => {
            console.log('Notification received:', notification);
            // Don't show global toast for ORDER_NEW events - let the VendorOrders page handle it
            if (notification.event === ORDER_EVENTS.NEW) {
              console.log('ORDER_NEW notification - letting page handle toast');
              return;
            }
            // Show toast for other notification types
            if (notification.message) {
              toast(notification.message, {
                icon: notification.type === 'error' ? '❌' : '✅'
              });
            }
          });

          // Set up order status listeners using standardized events
          socketService.on(ORDER_EVENTS.STATUS_UPDATE, (data: any) => {
            console.log('Order status update:', data);
            // Use customOrderId or orderNumber for display, fallback to orderId
            const displayOrderId = data.customOrderId || data.orderNumber || data.orderId;
            toast.success(`Order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} is now ${data.status}`);
          });

          // Set up KOT listeners using standardized events
          socketService.on(KOT_EVENTS.NEW, (data: any) => {
            console.log('New KOT:', data);
            toast.success(`New KOT #${data.kotNumber} received`);
          });

          socketService.on(KOT_EVENTS.UPDATED, (data: any) => {
            console.log('KOT update:', data);
            toast.success(`KOT #${data.kotNumber} updated: ${data.status}`);
          });

        } catch (error: any) {
          console.error('Failed to initialize socket connection:', error);
          if (!mounted) return;
          
          // Silent fail — log only, no error toasts for socket issues
          console.warn('Socket connection failed:', error.message || error);
          setIsConnected(false);
        }
      }
    };

    initializeSocket();

    return () => {
      mounted = false;
      if (isConnected) {
        socketService.disconnect();
        setIsConnected(false);
      }
    };
  }, [token, user]);

  // Memoize all callback functions to prevent unnecessary re-renders and effect re-runs
  const subscribeToOrder = useCallback((orderId: number) => socketService.subscribeToOrder(orderId), []);
  const unsubscribeFromOrder = useCallback((orderId: number) => socketService.unsubscribeFromOrder(orderId), []);
  const subscribeToKitchen = useCallback((kitchenId: number) => socketService.subscribeToKitchen(kitchenId), []);
  const unsubscribeFromKitchen = useCallback((kitchenId: number) => socketService.unsubscribeFromKitchen(kitchenId), []);
  const subscribeToVendor = useCallback((vendorId: number, callback?: (data: any) => void) => {
    socketService.subscribeToVendor(vendorId);
    if (callback) {
      socketService.on(VENDOR_EVENTS.UPDATE, callback);
    }
  }, []);
  const unsubscribeFromVendor = useCallback((vendorId: number) => socketService.unsubscribeFromVendor(vendorId), []);
  const subscribeToTable = useCallback((tableNumber: string, vendorId: number) => socketService.subscribeToTable(tableNumber, vendorId), []);
  const unsubscribeFromTable = useCallback((tableNumber: string, vendorId: number) => socketService.unsubscribeFromTable(tableNumber, vendorId), []);
  const emit = useCallback((event: string, data: any) => socketService.emit(event, data), []);
  const on = useCallback((event: string, callback: Function) => socketService.on(event, callback), []);
  const off = useCallback((event: string, callback: Function) => socketService.off(event, callback), []);

  const contextValue: SocketContextType = useMemo(() => ({
    isConnected,
    subscribeToOrder,
    unsubscribeFromOrder,
    subscribeToKitchen,
    unsubscribeFromKitchen,
    subscribeToVendor,
    unsubscribeFromVendor,
    subscribeToTable,
    unsubscribeFromTable,
    emit,
    on,
    off,
  }), [
    isConnected,
    subscribeToOrder,
    unsubscribeFromOrder,
    subscribeToKitchen,
    unsubscribeFromKitchen,
    subscribeToVendor,
    unsubscribeFromVendor,
    subscribeToTable,
    unsubscribeFromTable,
    emit,
    on,
    off
  ]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};